import {useCallback, useEffect, useRef} from 'react'

import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'

import {fireHopperEvent} from '../api/hopperAdsApi'

import type {HopperAd} from '../types'

const VIEW_THRESHOLD = 0.5

interface UseHopperAdTrackingParams {
  ad: HopperAd | null
  entity: Entity
  page: Page
}

interface UseHopperAdTrackingResult {
  impressionRef: (node?: Element) => void
  trackClick: () => void
}

/**
 * Hook to handle Hopper ad tracking (impressions and clicks)
 *
 * - Fires impression event when ad is 50% visible
 * - Provides trackClick callback for CTA clicks
 * - Fires both HTS events and internal Vio events
 */
export const useHopperAdTracking = ({
  ad,
  entity,
  page
}: UseHopperAdTrackingParams): UseHopperAdTrackingResult => {
  const dispatchTeamEvent = useDispatchTeamEvent()
  const hasTrackedImpression = useRef(false)

  const {ref: inViewRef, inView} = useInView({
    threshold: VIEW_THRESHOLD,
    triggerOnce: true
  })

  // Track impression when ad comes into view
  useEffect(() => {
    if (!inView || !ad || hasTrackedImpression.current) return

    hasTrackedImpression.current = true

    // Fire HTS impression event
    fireHopperEvent({
      eventType: 'Impression',
      token: ad.trackingData
    })

    // Fire internal Vio event
    if (entity === Entity.HopperCarousel) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HopperCarousel,
          action: Action.Viewed,
          page
        })
      )
    } else {
      dispatchTeamEvent(
        trackEvent({
          category: Category.System,
          entity,
          action: Action.Displayed,
          page
        })
      )
    }
  }, [inView, ad, dispatchTeamEvent, entity, page])

  // Track click
  const trackClick = useCallback(() => {
    if (!ad) return

    // Fire HTS click event
    fireHopperEvent({
      eventType: 'Click',
      token: ad.trackingData
    })

    // Fire internal Vio event
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity,
        action: Action.Clicked,
        page
      })
    )
  }, [ad, dispatchTeamEvent, entity, page])

  return {
    impressionRef: inViewRef,
    trackClick
  }
}
